import { useEffect, useMemo, useRef } from "react";
import { useGithubContributions } from "../../hooks/useGithubContributions";
import type { ContributionDay } from "../../types/githubContributions";

const DAY_LABELS = ["", "Mon", "", "Wed", "", "Fri", ""];
const MONTH_NAMES = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

interface Week {
  weekIndex: number;
  days: (ContributionDay | null)[];
  monthLabel: string | null;
}

function buildWeeks(days: ContributionDay[]): Week[] {
  if (days.length === 0) return [];

  const firstDate = new Date(`${days[0].date}T00:00:00`);
  const leadingBlanks = firstDate.getDay();
  const padded: (ContributionDay | null)[] = [
    ...Array(leadingBlanks).fill(null),
    ...days,
  ];

  const weeks: Week[] = [];
  let lastMonth = -1;
  for (let i = 0; i < padded.length; i += 7) {
    const weekDays = padded.slice(i, i + 7);
    const firstRealDay = weekDays.find((d): d is ContributionDay => d !== null);
    let monthLabel: string | null = null;
    if (firstRealDay) {
      const month = new Date(`${firstRealDay.date}T00:00:00`).getMonth();
      if (month !== lastMonth) {
        monthLabel = MONTH_NAMES[month];
        lastMonth = month;
      }
    }
    weeks.push({ weekIndex: weeks.length, days: weekDays, monthLabel });
  }
  return weeks;
}

function ContributionGraph() {
  const { data, status } = useGithubContributions();
  const weeks = useMemo(() => (data ? buildWeeks(data.days) : []), [data]);
  const graphRef = useRef<HTMLDivElement>(null);

  // Open on the most recent weeks when the graph is wider than its container.
  useEffect(() => {
    const el = graphRef.current;
    if (el) el.scrollLeft = el.scrollWidth;
  }, [data]);

  if (status === "loading") {
    return <p className="activity-status">Loading contribution graph…</p>;
  }

  if (status === "error" || !data) {
    return (
      <p className="activity-status">
        Couldn't load the contribution graph right now.
      </p>
    );
  }

  return (
    <div className="contribution-graph" ref={graphRef}>
      <div className="contribution-grid">
        <div className="contribution-day-labels">
          {DAY_LABELS.map((label, i) => (
            <span key={i}>{label}</span>
          ))}
        </div>
        <div className="contribution-weeks-wrap">
          <div className="contribution-months">
            {weeks.map((week) => (
              <span key={week.weekIndex} className="contribution-month-label">
                {week.monthLabel ?? ""}
              </span>
            ))}
          </div>
          <div className="contribution-weeks">
            {weeks.map((week) => (
              <div className="contribution-week" key={week.weekIndex}>
                {week.days.map((day, i) =>
                  day ? (
                    <span
                      key={day.date}
                      className={`contribution-cell level-${day.level}`}
                      title={`${day.count} contribution${
                        day.count === 1 ? "" : "s"
                      } on ${day.date}`}
                    />
                  ) : (
                    <span key={i} className="contribution-cell is-empty" />
                  )
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="contribution-footer">
        <span>{data.total} contributions in the last year</span>
        <div className="contribution-legend">
          <span>Less</span>
          <span className="contribution-cell level-0" />
          <span className="contribution-cell level-1" />
          <span className="contribution-cell level-2" />
          <span className="contribution-cell level-3" />
          <span className="contribution-cell level-4" />
          <span>More</span>
        </div>
      </div>
    </div>
  );
}

export default ContributionGraph;
